// Semua request ke bot Disbox lewat sini: token login ikut dikirim otomatis,
// dan kalau bot membalas "sesi tidak valid" (token habis/dicabut), app
// diarahkan ke halaman login.
import { BOT_API_URL } from './config.js';

const ACCOUNT_KEY = 'disbox_account';

// Mode publik (halaman /nonton): tanpa login. Endpoint file diarahkan ke
// API publik bot (/a/<route>/<path>, hanya folder anime) dan token tidak
// pernah dikirim.
const PUBLIC_ROUTES = /^\/(stream|probe|subtitles|remux)\/[^/?]+(\/[^?]*)?(\?.*)?$/;
let publicBase = null;

export function setPublicMode(base) {
    publicBase = base;
}

export function isPublicMode() {
    return Boolean(publicBase);
}

// Alamat API publik: lewat alamat yang sama (Caddy meneruskan /a ke bot), atau
// langsung ke bot di port 4000 kalau web dibuka dari serve-web (port 3000).
export function publicApiBase() {
    if (window.location.port === '3000') return `${BOT_API_URL}/a`;
    return `${window.location.origin}/a`;
}

export function getAccount() {
    try {
        return JSON.parse(localStorage.getItem(ACCOUNT_KEY));
    } catch {
        return null;
    }
}

export function saveAccount(account) {
    localStorage.setItem(ACCOUNT_KEY, JSON.stringify(account));
}

export function getAuthToken() {
    const account = getAccount();
    return account && account.token ? account.token : null;
}

// Akun yang login SEBELUM pembaruan keamanan belum punya token - wajib login ulang sekali.
export function hasValidSession() {
    const account = getAccount();
    return Boolean(account && account.token && account.webhookUrls && (!account.tokenExpiresAt || account.tokenExpiresAt > Date.now()));
}

export class AuthError extends Error {
    constructor(message) {
        super(message);
        this.name = 'AuthError';
    }
}

let authFailureHandler = null;
export function onAuthFailure(handler) {
    authFailureHandler = handler;
}

export function withToken(url) {
    if (publicBase) return url;
    const token = getAuthToken();
    if (!token) return url;
    return `${url}${url.includes('?') ? '&' : '?'}t=${encodeURIComponent(token)}`;
}

export function encodePath(path) {
    return String(path || '').split('/').filter(Boolean).map(encodeURIComponent).join('/');
}

// URL lengkap (dengan token) untuk endpoint file di bot, contoh:
// fileUrl('stream', userId, '/Anime/ep1.mkv', { dl: 1 })
export function fileUrl(route, userId, path, params = {}) {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== null && value !== '') query.set(key, String(value));
    }
    const qs = query.toString();
    if (publicBase) return `${publicBase}/${route}/${encodePath(path)}${qs ? `?${qs}` : ''}`;
    return withToken(`${BOT_API_URL}/${route}/${userId}/${encodePath(path)}${qs ? `?${qs}` : ''}`);
}

export async function botFetch(pathWithQuery, { method = 'GET', body, headers = {}, timeoutMs = 30000, signal } = {}) {
    const controller = new AbortController();
    const timer = timeoutMs ? setTimeout(() => controller.abort(), timeoutMs) : null;
    const onAbort = () => controller.abort();
    if (signal) signal.addEventListener('abort', onAbort);
    try {
        let url = withToken(`${BOT_API_URL}${pathWithQuery}`);
        if (publicBase) {
            const match = PUBLIC_ROUTES.exec(pathWithQuery);
            if (!match || method !== 'GET') throw new Error('Tidak tersedia di halaman publik.');
            url = `${publicBase}/${match[1]}${match[2] || ''}${match[3] || ''}`;
        }
        const response = await fetch(url, { method, body, headers, signal: controller.signal });
        if (response.status === 401 || response.status === 403) {
            const data = await response.clone().json().catch(() => ({}));
            if (data.code === 'AUTH') {
                if (authFailureHandler) authFailureHandler(data.error);
                throw new AuthError(data.error || 'Sesi login habis.');
            }
        }
        return response;
    } finally {
        if (timer) clearTimeout(timer);
        if (signal) signal.removeEventListener('abort', onAbort);
    }
}

// Body JSON untuk dikirim ke bot. JSON besar (index ratusan ribu file bisa
// sekitar 100 MB) dikompres gzip dulu, jadi yang dikirim kira-kira 7x lebih
// kecil. Browser lama tanpa CompressionStream mengirim apa adanya.
export async function jsonRequestBody(value) {
    const text = JSON.stringify(value);
    const headers = { 'Content-Type': 'application/json' };
    const Compression = typeof window !== 'undefined' ? window.CompressionStream : undefined;
    if (text.length > 256 * 1024 && typeof Compression === 'function') {
        try {
            const body = await new Response(new Blob([text]).stream().pipeThrough(new Compression('gzip'))).blob();
            return { body, headers: { ...headers, 'Content-Encoding': 'gzip' } };
        } catch {
            // Kirim tanpa kompresi.
        }
    }
    return { body: text, headers };
}

export async function botJson(pathWithQuery, options = {}) {
    const init = { ...options };
    if (init.json !== undefined) {
        init.body = JSON.stringify(init.json);
        init.headers = { ...(init.headers || {}), 'Content-Type': 'application/json' };
        delete init.json;
    }
    const response = await botFetch(pathWithQuery, init);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        const error = new Error(data.error || `Bot membalas status ${response.status}`);
        error.status = response.status;
        throw error;
    }
    return data;
}

// Token login berlaku 30 hari; diperpanjang otomatis saat app dibuka kalau
// tinggal kurang dari seminggu.
export async function refreshSessionIfNeeded() {
    const account = getAccount();
    if (!account || !account.token) return;
    const remaining = (account.tokenExpiresAt || 0) - Date.now();
    if (remaining > 7 * 24 * 60 * 60 * 1000) return;
    try {
        const data = await botJson('/auth/refresh', { method: 'POST' });
        saveAccount({
            ...account,
            token: data.token,
            tokenExpiresAt: data.tokenExpiresAt,
            webhookUrls: data.webhookUrls || account.webhookUrls,
            uploadWebhooks: data.uploadWebhooks === undefined ? account.uploadWebhooks : data.uploadWebhooks,
        });
    } catch (error) {
        if (error instanceof AuthError) throw error;
        // Bot sedang tidak bisa dihubungi: coba lagi lain kali.
    }
}

// Setelan akun (jumlah webhook & berapa yang dipakai upload) disimpan di bot,
// jadi sama di semua perangkat. Hasilnya juga disimpan ke akun lokal.
export async function fetchAccountSettings(userId) {
    const data = await botJson(`/account/${userId}/settings`);
    rememberAccountSettings(data);
    return data;
}

export async function updateAccountSettings(userId, changes) {
    const data = await botJson(`/account/${userId}/settings`, { method: 'PUT', json: changes, timeoutMs: 120000 });
    rememberAccountSettings(data);
    return data;
}

// Discord menolak potongan upload sebesar `bytes`: bot menurunkan ukuran
// potongan akun. Membalas setelan terbaru.
export function reportUploadTooLarge(userId, bytes) {
    return botJson(`/account/${userId}/upload-limit/too-large`, { method: 'POST', json: { bytes } });
}

// Bot mencoba mengirim lampiran uji ke Discord untuk mencari ukuran potongan
// terbesar yang diterima, lalu menyimpannya.
export async function probeUploadLimit(userId) {
    const data = await botJson(`/account/${userId}/upload-limit/probe`, { method: 'POST', json: {}, timeoutMs: 5 * 60 * 1000 });
    rememberAccountSettings(data);
    return data;
}

function rememberAccountSettings(data) {
    const account = getAccount();
    if (!account) return;
    saveAccount({
        ...account,
        webhookUrls: Array.isArray(data.webhookUrls) && data.webhookUrls.length > 0 ? data.webhookUrls : account.webhookUrls,
        // null = semua webhook (ikut bertambah kalau webhook ditambah).
        uploadWebhooks: data.uploadWebhooksAll ? null : data.uploadWebhooks,
        uploadChunkBytes: data.uploadChunkBytes || account.uploadChunkBytes,
    });
}

// ===================== Keamanan akun =====================

export function fetchSecurity() {
    return botJson('/auth/security');
}

export function logoutAllDevices() {
    return botJson('/auth/logout-all', { method: 'POST', json: {} });
}

// Logout di halaman /web/drive (bisa dibuka dari perangkat mana pun): selain
// sesi, daftar file yang tersimpan di browser ikut dihapus.
export async function wipeLocalData() {
    clearLocalSession();
    for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && key.startsWith('disbox')) localStorage.removeItem(key);
    }
    try {
        sessionStorage.clear();
    } catch { /* abaikan */ }
    if (typeof indexedDB !== 'undefined') {
        await new Promise((resolve) => {
            const request = indexedDB.deleteDatabase('disbox');
            request.onsuccess = request.onerror = request.onblocked = () => resolve();
        });
    }
}

export function clearLocalSession() {
    for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && key.startsWith('disbox_') && key.endsWith('_enckey')) {
            localStorage.removeItem(key);
        }
    }
    localStorage.removeItem(ACCOUNT_KEY);
    localStorage.removeItem('webhookUrl');
}
